import PropTypes from "prop-types";
import { useFormContext } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { BaseForm, FormCombobox, FormDatePicker } from "components/BaseForm";
import CampaignService from "services/Campaign";
import RevenueOpportunityService from "services/RevenueOpportunities";
import RevenueOpportunityStatusPresetService from "services/RevenueOpportunityStatusPreset";

const schema = z.object({
  status_id: z
    .number()
    .nullable()
    .refine(value => value != null, "Status is required"),
  effective_date: z
    .string()
    .nullable()
    .refine(Boolean, "Effective Date is required"),
});

const fetchStatuses = async campaignId => {
  let statuses = null;
  if (campaignId) {
    const campaign = await CampaignService.getCampaign(campaignId);
    const presetId = campaign.info?.revenue_status_preset;
    if (presetId) {
      const preset =
        await RevenueOpportunityStatusPresetService.getRevenueOpportunityStatusPreset(
          presetId
        );
      statuses = preset.info?.revenue_opportunity_statuses;
    }
  }
  // A preset whose statuses have all been deleted constrains nothing, so fall
  // back to the full list rather than offering an empty dropdown.
  if (!statuses?.length) {
    statuses =
      await RevenueOpportunityService.getRevenueOpportunityStatusesForFilters();
  }
  return statuses.map(({ id, name }) => ({ key: id, value: id, text: name }));
};

const StatusFields = ({ statusOptions }) => {
  const { setValue } = useFormContext();
  return (
    <>
      <FormCombobox
        name="status_id"
        label="Status"
        required
        options={statusOptions}
        placeholder="Pick a Revenue Opportunity Status"
        // A new status takes effect on its own date, so the old one is cleared.
        onValueChange={() =>
          setValue("effective_date", null, { shouldDirty: true })
        }
      />
      <FormDatePicker name="effective_date" label="Effective Date" required />
    </>
  );
};

StatusFields.propTypes = {
  statusOptions: PropTypes.array.isRequired,
};

/**
 * Sets the status and effective date of one or more Revenue Opportunities.
 * The status options follow the campaign's status preset.
 */
const RevenueOpportunityStatusDialog = ({
  open,
  onOpenChange,
  revenueOpportunityIds,
  campaignId,
  statusId,
  effectiveDate,
  onSaved,
}) => {
  const { data: statusOptions, isPending } = useQuery({
    queryKey: ["revenue-opportunity-status-dialog", campaignId ?? null],
    queryFn: () => fetchStatuses(campaignId),
    enabled: open,
  });

  const setStatus = useMutation({
    mutationFn: values =>
      RevenueOpportunityService.setRevenueOpportunityStatus({
        ...values,
        revenue_opportunity_id: revenueOpportunityIds,
      }),
  });

  const handleSubmit = async values => {
    const isChanged =
      values.status_id !== statusId || values.effective_date !== effectiveDate;
    if (isChanged) {
      await setStatus.mutateAsync(values);
    }
  };

  const handleSuccess = () => {
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw:sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Revenue Opportunity Status</DialogTitle>
        </DialogHeader>
        {isPending ? (
          <div className="tw:flex tw:justify-center tw:p-6">
            <Spinner />
          </div>
        ) : (
          <BaseForm
            schema={schema}
            defaultValues={{
              status_id: statusId ?? null,
              effective_date: effectiveDate ?? null,
            }}
            onSubmit={handleSubmit}
            onSuccess={handleSuccess}
            onCancel={() => onOpenChange(false)}
            submitButtonText="Apply"
          >
            <StatusFields statusOptions={statusOptions} />
          </BaseForm>
        )}
      </DialogContent>
    </Dialog>
  );
};

RevenueOpportunityStatusDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  revenueOpportunityIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  campaignId: PropTypes.number,
  statusId: PropTypes.number,
  effectiveDate: PropTypes.string,
  onSaved: PropTypes.func.isRequired,
};

export default RevenueOpportunityStatusDialog;
